import React, { useEffect, useRef, useState } from 'react';
import './OpponentCounterStrikeDialog.css';
import { OPPONENT_COUNTER_STRIKE_REVEAL_MS } from './counterStrikeReveal';

/**
 * Opponent Counter Strike Dialog Component
 * Displays when the opponent has a pending Counter Strike order
 * Blocks the player from interacting with the game until counterstrike is resolved
 *
 * Mobile support: When isMobile is true, displays with smaller font sizes
 * to fit the 932x430 mobile viewport.
 *
 * Stays visible for at least OPPONENT_COUNTER_STRIKE_REVEAL_MS so the strike-back
 * beat is readable even when the bot resolves quickly on the server.
 */
const OpponentCounterStrikeDialog = ({ show, ws, gameId, gameState, userId, isMobile = false }) => {
  const [visible, setVisible] = useState(false);
  const showStartedAtRef = useRef(null);
  const hideTimerRef = useRef(null);
  const timeoutRef = useRef(null);
  const hasTriggeredAutoKeep = useRef(false);

  useEffect(() => {
    if (show) {
      if (!visible) {
        showStartedAtRef.current = Date.now();
        setVisible(true);
      }
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    } else if (visible) {
      const startedAt = showStartedAtRef.current;
      if (startedAt == null) {
        setVisible(false);
        return;
      }
      const elapsed = Date.now() - startedAt;
      const remaining = OPPONENT_COUNTER_STRIKE_REVEAL_MS - elapsed;
      if (remaining <= 0) {
        setVisible(false);
        showStartedAtRef.current = null;
      } else {
        hideTimerRef.current = setTimeout(() => {
          setVisible(false);
          showStartedAtRef.current = null;
          hideTimerRef.current = null;
        }, remaining);
      }
    }

    return () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };
  }, [show, visible]);

  useEffect(() => {
    if (!visible || !gameState) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      hasTriggeredAutoKeep.current = false;
      return;
    }

    const opponentId = gameState.players?.find((id) => id !== userId);
    if (!opponentId) {
      return;
    }

    const isBot = opponentId === 'bot' || opponentId === 'campaign_bot';
    if (!isBot) {
      return;
    }

    const opponentState = gameState.state?.[opponentId];
    const pendingCounterStrike = opponentState?.pendingCounterStrike || [];

    if (pendingCounterStrike.length === 0) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      hasTriggeredAutoKeep.current = false;
      return;
    }

    if (!hasTriggeredAutoKeep.current) {
      timeoutRef.current = setTimeout(() => {
        console.warn(
          `⏰ Opponent Counter Strike: 5-second timeout reached for bot ${opponentId} - backend should auto-resolve`
        );
        hasTriggeredAutoKeep.current = true;
      }, 5000);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [visible, gameState, userId]);

  if (!visible) {
    return null;
  }

  return (
    <div className="opponent-counter-strike-overlay" data-no-battle-flashlight="true">
      <div
        className="opponent-counter-strike-dialog"
        style={
          isMobile
            ? {
                transform: 'scale(0.5)',
                transformOrigin: 'center center',
              }
            : {}
        }
      >
        <div className="opponent-counter-strike-header">
          <h2 style={isMobile ? { fontSize: '28px' } : {}}>
            Your opponent has a chance to strike back!
          </h2>
        </div>
      </div>
    </div>
  );
};

export default OpponentCounterStrikeDialog;
